import React, { useState, useRef } from 'react';
import axios from 'axios';
import './App.css';

function App() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [svgResult, setSvgResult] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [mode, setMode] = useState('auto');
  const [detectedMode, setDetectedMode] = useState(null);

  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      setError('Please select a JPG or PNG image.');
      return;
    }
    setSelectedFile(file);
    setError(null);
    setSvgResult(null);
    setDetectedMode(null);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleConvert = async () => {
    if (!selectedFile) {
      setError('Please select an image first.');
      return;
    }

    setIsConverting(true);
    setError(null);

    const formData = new FormData();
    formData.append('image', selectedFile);
    formData.append('mode', mode);

    try {
      const response = await axios.post('/api/convert', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setSvgResult(response.data.svg);
        setDetectedMode(response.data.detectedMode);
      } else {
        setError('Conversion failed. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Conversion failed.');
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownloadSVG = () => {
    if (!svgResult) return;
    const blob = new Blob([svgResult], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedFile.name.split('.')[0]}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <h1>SVG Vectorizer</h1>
          <p>Convert images to vector graphics</p>
        </header>

        <main className="main-content">
          {/* Upload */}
          <section className="upload-section">
            <div
              className={`upload-zone ${isDragging ? 'dragover' : ''}`}
              onClick={() => fileInputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFileSelect(e.dataTransfer.files[0]); }}
            >
              <div className="upload-icon">📁</div>
              <h3>Drop image here or click</h3>
              <p>JPG or PNG</p>
              <input
                ref={fileInputRef}
                type="file"
                className="file-input"
                accept="image/jpeg,image/jpg,image/png"
                onChange={(e) => handleFileSelect(e.target.files[0])}
              />
            </div>
            {selectedFile && (
              <p className="selected-file">Selected: {selectedFile.name}</p>
            )}
          </section>

          {/* Mode selector */}
          {selectedFile && (
            <section className="controls-section">
              <div className="mode-selector">
                <label>Image Type:</label>
                <div className="mode-buttons">
                  <button
                    className={`mode-btn ${mode === 'auto' ? 'active' : ''}`}
                    onClick={() => setMode('auto')}
                  >
                    Auto Detect
                  </button>
                  <button
                    className={`mode-btn ${mode === 'lineart' ? 'active' : ''}`}
                    onClick={() => setMode('lineart')}
                  >
                    Line Art (outlines)
                  </button>
                  <button
                    className={`mode-btn ${mode === 'filled' ? 'active' : ''}`}
                    onClick={() => setMode('filled')}
                  >
                    Filled Shapes
                  </button>
                </div>
              </div>

              <button
                className="convert-button"
                onClick={handleConvert}
                disabled={isConverting}
              >
                {isConverting ? 'Converting...' : 'Convert to SVG'}
              </button>
            </section>
          )}

          {/* Error */}
          {error && <div className="error-message">{error}</div>}

          {/* Detected mode info */}
          {detectedMode && (
            <div className="detected-mode">
              Processed as: <strong>{detectedMode === 'lineart' ? 'Line Art' : 'Filled Shapes'}</strong>
            </div>
          )}

          {/* Preview */}
          {(previewUrl || svgResult) && (
            <section className="preview-section">
              <div className="preview-grid">
                <div className="preview-box">
                  <h3>Original</h3>
                  <div className="preview-content">
                    {previewUrl && <img src={previewUrl} alt="Original" />}
                  </div>
                </div>
                <div className="preview-box">
                  <h3>Result</h3>
                  <div className="preview-content">
                    {isConverting ? (
                      <div className="loading">Converting...</div>
                    ) : svgResult ? (
                      <div dangerouslySetInnerHTML={{ __html: svgResult }} />
                    ) : (
                      <span className="empty">Click convert</span>
                    )}
                  </div>
                </div>
              </div>

              {svgResult && (
                <div className="download-section">
                  <button className="download-button" onClick={handleDownloadSVG}>
                    Download SVG
                  </button>
                </div>
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
